/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Error from '../../../Components/Error';
import EventPointMap from '../../../Components/EventPointMap';
import BookingService from '../../../Services/BookingService';
import { formatDateTime as format } from '../../../Utils/timezone';
import getDriverStatusLabel from '../../../Utils/getDriverStatusLabel';
import { NO_POSITION_HINT, eventPoint, formatPosition } from '../../../Utils/eventPosition';
import {
  actorFor, carriesDevicePosition, detailsFor, driverEventRows, headingFor,
} from '../../../Utils/driverEventLog';
import {
  NO_SHOW_POSITION_MISSING,
  noShowCallText,
  noShowCheckpointRows,
  noShowNameSignText,
  noShowPhotoLabel,
  noShowPoint,
  noShowWaitText,
} from '../../../Utils/noShowEvidence';
import DriverOvertimeSection from './DriverOvertimeSection';
import DriverExpensesSection from './DriverExpensesSection';

// One terminal-log entry as the timeline shows it. `position` is the honest
// reading of metadata.lat/lon: real coordinates, or one sentence saying there
// are none — never the placeholder token whichever client wrote the event
// happened to use ('unavailable' from the portal, 'unknown' from older
// terminal builds, a bare 0). See Utils/eventPosition.js.
//
// `heading`, `actor` and `details` are the rest of what the row has always
// been carrying and never showed — the partner's refusal reason, the minutes
// on a waiting clock, the administrator who tapped this as the driver. One
// reader for all of it, written next to the writes: Utils/driverEventLog.js.
// `showPosition` is rule 2 there: a missing fix is only mentioned where one
// was ever sought.
const parseEvent = (event) => ({
  action: event.entry.action.replace(/_/g, ' '),
  date: format(new Date(event.Date)),
  position: formatPosition(event.entry.metadata),
  point: eventPoint(event.entry.metadata),
  metadata: event.entry.metadata,
  heading: headingFor(event.entry.action),
  actor: actorFor(event.entry.from, event.entry.metadata),
  details: detailsFor(event.entry.action, event.entry.metadata),
  showPosition: carriesDevicePosition(event.entry.action)
    || eventPoint(event.entry.metadata) !== null,
});

/**
 * THE GROUNDS OF A NO-SHOW — the one block on this page that has to survive
 * a partner reading it.
 *
 * A no-show bills the customer, so the partner who sold the trip contests
 * it, and what they ask is: how long did your driver wait, from when did
 * you count, did anyone ring the passenger, and where was the car. All four
 * are here, in that order, next to the event itself.
 *
 * WHAT IS NOT PROVEN IS STATED. An unticked checkpoint is shown as unticked
 * rather than left out; a report with no GPS fix says so instead of showing
 * an empty line that reads like agreement; and a claim that rested on the
 * office's say-so rather than on a landing time says which.
 *
 * `evidence` is the copy the portal wrote into the event's own metadata, so
 * it is whatever was true at the moment the claim was made — not a later
 * re-reading of the booking. The NAME SIGN is deliberately not in here: it
 * is its own event (`NAME SIGN ANSWERED`) on this same timeline, with its
 * own time and position, because it is answered long before a claim exists
 * and belongs to trips that never produce one. The photos come as short-lived presigned links
 * added when the events are read (BookingsController.getDriverEvents); they
 * live in a private bucket and there is no URL to keep.
 */
function NoShowEvidence({ evidence, photoUrls = [] }) {
  if (!evidence) return null;

  const point = noShowPoint(evidence);

  return (
    <div className="border-start border-3 ps-3 my-2" data-testid="no-show-evidence">
      <p className="fw-bold mb-1">No-show evidence</p>

      <p className="mb-1">{noShowWaitText(evidence)}</p>
      {evidence.landed_at && (
        <p className="text-muted small mb-1">
          {'Landed '}
          {format(new Date(evidence.landed_at))}
          {' • Local'}
        </p>
      )}
      {evidence.office_agreed && <p className="mb-1">Agreed with the office beforehand</p>}

      <ul className="list-unstyled mb-1">
        {noShowCheckpointRows(evidence).map((row) => (
          <li key={row.key}>
            <i className={row.confirmed ? 'bi bi-check-circle-fill text-success me-1' : 'bi bi-x-circle text-danger me-1'} />
            {row.label}
          </li>
        ))}
      </ul>

      <p className="mb-1">{noShowCallText(evidence)}</p>

      {evidence.note && (
        <p className="mb-1">
          <b>Driver&apos;s note</b>
          {' '}
          {evidence.note}
        </p>
      )}

      {point ? (
        <>
          <p className="text-muted mb-1">{`${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`}</p>
          <EventPointMap point={point} label="No-show reported" />
        </>
      ) : (
        <p className="text-danger mb-1" title={NO_POSITION_HINT}>{NO_SHOW_POSITION_MISSING}</p>
      )}

      {/* Each link says WHAT the photo was filed to prove; one filed before
          the slots existed reads as uncategorised rather than mislabelled. */}
      {photoUrls.map((photo, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={`no-show-photo-${index}`} className="mb-1">
          <a href={photo.url} target="_blank" rel="noreferrer">
            {`${noShowPhotoLabel(photo.category)} (link expires in 15 minutes)`}
          </a>
        </p>
      ))}
    </div>
  );
}

/** The passenger terminal's log for this booking — every step, overtime and review. */
function DriverEventsTimeline({ order }) {
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  // Which rows have their map open. Collapsed by default, and one open map
  // does not close another — two events are often compared against each other.
  const [openMaps, setOpenMaps] = useState(() => new Set());
  // Which collapsed runs of routine rows are open. Same idea, different
  // question: `openMaps` is one event's map, this is a run of events.
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const toggleMap = (index) => setOpenMaps((previous) => {
    const next = new Set(previous);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    return next;
  });

  const toggleGroup = (key) => setOpenGroups((previous) => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  useEffect(() => {
    let live = true;
    setError('');
    setOpenMaps(new Set());
    setOpenGroups(new Set());
    BookingService.getDriverEvents(order)
      .then((response) => { if (live) setEvents(response?.data?.data || []); })
      .catch((err) => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [order?._id]);

  return (
    <>
      <h6 className="fw-bold mt-3">Events</h6>
      <Error error={error} setError={setError} />
      {events.length === 0 ? (
        <p className="text-muted">No events available.</p>
      ) : (
        <ul className="timeline">
          {driverEventRows(events, openGroups).map((row) => {
            if (row.kind === 'group') {
              return (
                <CollapsedRun key={row.key} row={row} onToggle={() => toggleGroup(row.key)} />
              );
            }

            const { event, index } = row;
            const parsedEvent = parseEvent(event);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index} className="timeline-item">
                <div className="timeline-icon">
                  <i className="bi bi-check-circle-fill text-success" />
                </div>
                <div className="timeline-content">
                  <h6 className="fw-bold">{parsedEvent.heading}</h6>
                  {parsedEvent.action === 'NEW EVENT' && <p>{getDriverStatusLabel(parsedEvent.metadata.event_type)}</p>}
                  {/* "Was the driver visibly there, and with whose sign" —
                      answered while they waited, so it is here with its own
                      time and position rather than inside a claim that may
                      never be made. */}
                  {parsedEvent.action === 'NAME SIGN ANSWERED' && (
                    <p data-testid="name-sign-answered">{noShowNameSignText(parsedEvent.metadata)}</p>
                  )}
                  {(parsedEvent.action === 'NEW EVENT' && parsedEvent.metadata.car) && (
                  <p>
                    <span>
                      <b>Plate Number</b>
                      {' '}
                      {parsedEvent.metadata.car.plate_number}
                    </span>
                    <span className="ms-2">
                      <b>Type</b>
                      {' '}
                      {parsedEvent.metadata.car.type}
                    </span>
                  </p>
                  )}
                  {(parsedEvent.action === 'NEW EVENT' && parsedEvent.metadata.driver) && (
                  <p>
                    <span>
                      <b>Name</b>
                      {' '}
                      {parsedEvent.metadata.driver.full_name}
                    </span>
                    <span className="ms-2">
                      <b>Phone Number</b>
                      {' '}
                      {parsedEvent.metadata.driver.phone_number}
                    </span>
                  </p>
                  )}
                  {(parsedEvent.action === 'NEW EVENT' && parsedEvent.metadata.event_type === 5) && (
                    <>
                      <NoShowEvidence
                        evidence={parsedEvent.metadata.no_show_evidence}
                        photoUrls={parsedEvent.metadata.no_show_photo_urls || []}
                      />
                      {/* Photos filed through the LEGACY passenger terminal,
                          which put them in the public assets bucket as
                          plain CloudFront URLs. Historic rows only — the
                          portal stores its evidence privately now. */}
                      {(parsedEvent.metadata.no_show_urls || []).map((url) => (
                        <p key={`url-${url}`}>
                          <a href={url} target="_blank" rel="noreferrer">{url}</a>
                        </p>
                      ))}
                    </>
                  )}
                  <EventDetails details={parsedEvent.details} />
                  <p className="text-muted mb-1">
                    {parsedEvent.date}
                    {' • Local • '}
                    {parsedEvent.actor}
                    {/* A position, and the sentence about not having one,
                        only where the driver's phone was ever asked for it.
                        Under the office's own approval "No location
                        recorded" would suggest a fix was sought and missed.
                        See Utils/driverEventLog's rule 2. */}
                    {parsedEvent.showPosition && ' • '}
                    {parsedEvent.showPosition && (
                    <span title={parsedEvent.point ? undefined : NO_POSITION_HINT}>
                      {parsedEvent.position}
                    </span>
                    )}
                    {parsedEvent.point && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2 align-baseline"
                        aria-expanded={openMaps.has(index)}
                        onClick={() => toggleMap(index)}
                        data-testid={`driver-event-map-toggle-${index}`}
                      >
                        {openMaps.has(index) ? 'Hide map' : 'Show map'}
                      </Button>
                    )}
                  </p>
                  {parsedEvent.point && openMaps.has(index) && (
                    <EventPointMap point={parsedEvent.point} label={parsedEvent.action} />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/**
 * What the event's metadata says, beyond its name and its time: the
 * partner's refusal in their own words, the minutes on a waiting clock, who
 * the booking moved from and to. `detailsFor` decides what there is to say;
 * a row with nothing to add renders nothing at all rather than an empty
 * heading, because on this tab a blank reads like "nothing happened".
 */
function EventDetails({ details }) {
  if (!details || details.length === 0) return null;

  return (
    <ul className="list-unstyled mb-1" data-testid="driver-event-details">
      {details.map((line) => (
        <li key={`${line.label}-${line.value}`}>
          {line.label && (
            <>
              <b>{line.label}</b>
              {' '}
            </>
          )}
          {line.value}
        </li>
      ))}
    </ul>
  );
}

/**
 * A run of rows that are individually true and collectively unreadable —
 * partner acknowledgements, location pings — behind one line and a count.
 *
 * The office is looking for the moment something went WRONG, and must not
 * have to scroll past a hundred confirmations to find it. Nothing is
 * dropped: the run opens on a click and each row renders in full. A
 * REFUSAL is never in here; that is the exception being hunted for.
 */
function CollapsedRun({ row, onToggle }) {
  return (
    <li className="timeline-item" data-testid={`driver-event-group-${row.key}`}>
      <div className="timeline-icon">
        <i className="bi bi-three-dots text-muted" />
      </div>
      <div className="timeline-content">
        <p className="text-muted mb-1">
          {row.heading}
          {' • '}
          {format(new Date(row.first.Date))}
          {row.count > 1 && ` – ${format(new Date(row.last.Date))}`}
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-2 align-baseline"
            aria-expanded={row.open}
            onClick={onToggle}
            data-testid={`driver-event-group-toggle-${row.key}`}
          >
            {row.open ? 'Hide' : `Show ${row.count}`}
          </Button>
        </p>
      </div>
    </li>
  );
}

/**
 * Order Details' "Driver events" tab — the one place per booking where its
 * trip claims are reviewed: overtime and receipts (approve / reject, then
 * "Create line item"), and the terminal log's timeline below.
 *
 * `claims` is the booking's useBookingClaims state, shared with the banner.
 */
function DriverEventsTab({ order, claims, onCreateLineItem }) {
  return (
    <div data-testid="driver-events-tab">
      <DriverOvertimeSection claims={claims} onCreateLineItem={onCreateLineItem} />
      <DriverExpensesSection claims={claims} onCreateLineItem={onCreateLineItem} />
      <DriverEventsTimeline order={order} />
    </div>
  );
}

export default DriverEventsTab;
